import { Button, HeaderBlock, MonetaryAmount, Prompt } from '@components'
import { PermissionBoundary, PermissionSet, useBusinessContext, usePermission } from '@context'
import { collectFromPages, deleteProduct, fetchProduct, IProduct } from '@query'
import { motion } from 'framer-motion'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaCopy, FaPen, FaTrash } from 'react-icons/fa'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'
import { ProductFormModal } from './ProductFormModal'
import { useVATCode } from '@root/query/hooks/useVATCodes'

interface Props {
  productId: number
  onClose: () => void
}

export const ProductDetails: React.FC<Props> = ({ productId, onClose }) => {
  const queryClient = useQueryClient()
  const { slug } = useBusinessContext()
  const [t] = useTranslation()
  const [showDelete, setShowDelete] = useState(false)
  const [showEdit, setShowEdit] = useState(false)
  const [showCopy, setShowCopy] = useState(false)
  const hasEditPermission = usePermission(PermissionSet.INVOICING_EDIT)

  const getCopyValues = (product: IProduct): Partial<IProduct> => ({
    name: product.name,
    unit: product.unit,
    amount: product.amount,
    vat_code: product.vat_code,
    vat_rate: product.vat_rate,
    is_vat_inclusive: product.is_vat_inclusive,
    ...(product.account_id != null && { account_id: product.account_id }),
    description: product.description
  })

  const { data } = useQuery({
    queryKey: [slug, 'products', productId],
    queryFn: () => fetchProduct({ slug, productId }),
    ...{
      enabled: !showDelete,
      initialData: () => {
        const queries = queryClient.getQueriesData({
          queryKey: [slug, 'products']
        })
        for (const [, queryData] of queries) {
          if (queryData) {
            const products = collectFromPages<IProduct>(queryData as any)
            const match = products.find(c => c.id === productId)
            if (match) return match
          }
        }
      }
    }
  })

  const { mutateAsync: deleteMutation, isPending: isDeleting } = useMutation({
    mutationFn: () => deleteProduct({ slug, productId }),
    ...{
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: [slug, 'products'] })
        onClose()
        setShowDelete(false)
      }
    }
  })

  const vatOption = useVATCode(data?.vat_code)
  const hideVatPercentage = vatOption?.tags.includes('taxfree')

  return (
    <StyledProductDetails>
      <HeaderBlock
        header={t('products.form.header')}
        subHeader={data?.name}
        menuItems={[
          ...(hasEditPermission
            ? [
                {
                  Icon: FaCopy,
                  name: t('products.copyProduct'),
                  onClick: () => setShowCopy(true)
                },
                {
                  Icon: FaTrash,
                  name: t('products.delete'),
                  intent: 'danger',
                  onClick: () => setShowDelete(true)
                }
              ]
            : [])
        ]}
      />

      <ScrollableContent>
        <DetailRow>
          <StyledHeader>{t('products.form.code')}</StyledHeader>
          <StyledValue>{data?.code || '-'}</StyledValue>
        </DetailRow>

        <DetailRow>
          <StyledHeader>{t('products.form.name')}</StyledHeader>
          <StyledValue>{data?.name}</StyledValue>
        </DetailRow>

        {data?.description && (
          <DetailRow>
            <StyledHeader>{t('products.form.description')}</StyledHeader>
            <StyledValue>{data.description}</StyledValue>
          </DetailRow>
        )}

        <DetailRow>
          <StyledHeader>{t('products.form.amountExclVat')}</StyledHeader>
          <StyledValue>
            <MonetaryAmount
              value={data?.vat_exclusive_amount}
              precision={{
                maximumFractionDigits: 3,
                minimumFractionDigits: 2
              }}
            />
          </StyledValue>
        </DetailRow>

        <DetailRow>
          <StyledHeader>{t('products.form.vatAmount')}</StyledHeader>
          <StyledValue>
            <MonetaryAmount
              value={data?.vat_amount}
              precision={{
                maximumFractionDigits: 3,
                minimumFractionDigits: 2
              }}
            />
          </StyledValue>
        </DetailRow>

        <DetailRow>
          <StyledHeader>{t('products.form.totalAmount')}</StyledHeader>
          <StyledValue>
            <MonetaryAmount
              value={data?.vat_inclusive_amount}
              precision={{
                maximumFractionDigits: 3,
                minimumFractionDigits: 2
              }}
            />
          </StyledValue>
        </DetailRow>

        <DetailRow>
          <StyledHeader>{t('products.form.vatCode')}</StyledHeader>
          <StyledValue>{t(vatOption?.label)}</StyledValue>
        </DetailRow>

        {!hideVatPercentage && (
          <DetailRow>
            <StyledHeader>{t('products.form.vatRate')}</StyledHeader>
            <StyledValue>{data?.vat_rate}%</StyledValue>
          </DetailRow>
        )}
      </ScrollableContent>

      <StyledFooter>
        <PermissionBoundary required={PermissionSet.INVOICING_EDIT}>
          {data && (
            <Button icon={<FaPen />} onClick={() => setShowEdit(true)}>
              {t('general.edit')}
            </Button>
          )}
        </PermissionBoundary>
      </StyledFooter>

      <ProductFormModal
        isVisible={showEdit}
        handleOnClose={() => setShowEdit(false)}
        product={data}
      />

      <ProductFormModal
        key="product-copy"
        isVisible={showCopy}
        handleOnClose={() => setShowCopy(false)}
        product={data ? getCopyValues(data) : undefined}
      />

      <Prompt
        isVisible={showDelete}
        title={t('products.deleteTitle')}
        description={t('products.deleteDescription')}
        buttons={[
          {
            text: t('general.delete'),
            icon: <FaTrash />,
            intent: 'danger',
            action: () => deleteMutation(),
            showSpinner: isDeleting,
            disabled: isDeleting
          }
        ]}
        onClose={() => setShowDelete(false)}
      />
    </StyledProductDetails>
  )
}

const StyledProductDetails = styled.div`
  height: 100%;
  background: ${({ theme }) => theme.colors.neutralWhite};
  display: flex;
  flex-direction: column;
`

const DetailRow = styled(motion.div).attrs({ layout: 'position' })`
  flex: 1;
  margin-bottom: ${({ theme }) => theme.spacing.sm}rem;
`

const StyledHeader = styled.p`
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  margin-bottom: ${({ theme }) => theme.spacing.xxs}rem;
  align-self: flex-start;
`

const StyledValue = styled.div`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  color: ${({ theme }) => theme.colors.neutralBlack};
  margin: 0;
  display: flex;
  align-items: center;

  svg {
    width: ${({ theme }) => theme.iconSize.xs}rem;
    height: ${({ theme }) => theme.iconSize.xs}rem;
    margin: 0;
    padding: 0;
  }
`

const ScrollableContent = styled.div`
  flex: 1;
  overflow: auto;
  padding: ${({ theme }) => theme.spacing.md}rem;
`

const StyledFooter = styled.div`
  padding: ${({ theme }) => theme.spacing.md}rem;
`
